<template>
  <fullscreen-dialog
    v-model="model"
    :title="`${toLatin(employee?.name || '')} ${toLatin(employee?.lastname || '')}, ID ${
      employee?.id || ''
    }`"
    :subtitle="toLatin(company?.company || '')"
    hide-footer
  >
    <template #default>
      <v-card-text class="py-4">
        <v-row align="center">
          <v-col>
            <month-picker
              v-model="date"
              @updatedMonthDates="monthDates = $event"
              color="white"
            />
          </v-col>
          <v-col cols="auto">
            <text-field
              v-model="search"
              kind="search"
              :label="$t('search')"
              style="width: 250px"
              color="white"
            />
          </v-col>
          <v-col cols="auto">
            <v-checkbox
              class="show-all-checkbox mt-4"
              :label="$t('show_all_days')"
              v-model="showAllDates"
              dense
            />
          </v-col>

          <v-col cols="auto">
            <dropdown
              :label="$t('view')"
              v-model="selectedLayout"
              :items="layouts"
              color="white"
              style="width: 280px"
            />
          </v-col>
          <v-col cols="auto">
            <print-button @click="print" />
          </v-col>
        </v-row>
        <v-data-table
          :headers="headers"
          :items="showAllDates ? ordersByDatesEntireMonth : filteredOrdersByDates"
          :search="search"
          :rows-per-page="[100]"
          :items-per-page="100"
          hide-default-footer
          class="ordera-box-shadow striped-table"
        >
          <template v-slot:item="{ item }">
            <tr>
              <td>{{ item.date }}</td>
              <td>{{ item.shift }}</td>
              <td v-if="!mealCategories.some(Boolean)">{{ toLatin(item.meal_name) }}</td>
              <template v-for="category in mealCategories">
                <td>
                  <span v-if="Array.isArray(item[category]) && item[category].length">
                    <template v-for="(meal, mealIndex) in item[category]">
                      <template v-if="selectedLayout <= 1">
                        <strong v-if="selectedLayout === 0"
                          >{{ meal.quantity }}x&nbsp;</strong
                        >
                        <v-tooltip bottom v-if="meal.label !== $t('total')">
                          <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on">{{ toLatin(meal.name) }}</span>
                          </template>
                          <span>{{ meal.id }}</span>
                        </v-tooltip>
                        <span v-else>{{ meal.label }}</span>
                      </template>
                      <strong v-else-if="selectedLayout === 2">{{
                        meal.quantity
                      }}</strong>
                      <template v-else>{{ meal.label }}</template>
                      <br v-if="mealIndex < item[category].length - 1" />
                    </template>
                  </span>
                </td>
              </template>
              <td>{{ item.total || "" }}</td>
              <td>{{ item.totalOrders || "" }}</td>
              <td>
                {{ item.total && item.totalOrders ? item.total * item.totalOrders : "" }}
              </td>
            </tr>
          </template>
          <template v-slot:body.append>
            <tr>
              <td style="font-weight: bold" colspan="2">{{ $t("total") }}</td>
              <template v-for="category in mealCategories">
                <td style="font-weight: bold">
                  {{ totalByCategory[category] }}
                </td>
              </template>
              <!-- <td></td> -->
              <td style="font-weight: bold">
                {{ totalQuantity }}
              </td>
              <td style="font-weight: bold">
                {{ ordersByDates.reduce((sum, row) => sum + row.totalOrders, 0) }}
              </td>
              <td style="font-weight: bold">{{ totalOrders }}</td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </template>
  </fullscreen-dialog>
</template>

<script>
import reportService from "@/services/report"; // Add this import
import hasModel from "@/mixins/hasModel";
import hasPrinting from "@/mixins/hasPrinting";
import hasFoodOrders from "@/mixins/report/hasFoodOrders";
import hasSnackbar from "@/mixins/hasSnackbar";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";

export default {
  name: "ReportEmployeeOrderHistoryDialog",
  mixins: [hasModel, hasPrinting, hasFoodOrders, hasSnackbar],
  components: {
    FullscreenDialog,
  },
  props: {
    employee: {
      type: Object,
      default: () => ({}),
    },
    groupMeal: {
      type: Boolean,
      default: false,
    },
    company: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      allDay: false,
      date: new Date().toISOString().substr(0, 7),
      modal: false,
      search: "",
      ordersByDates: [],
      monthDates: [],
      showAllDates: false,
      selectedLayout: 0,
      selectedRestaurants: [],
    };
  },
  created() {
    const dateTo = this.filters.dates.to;
    if (dateTo) {
      this.date = new Date(dateTo).toISOString().substr(0, 7);
    }
  },
  computed: {
    layouts() {
      return [
        { text: this.$t("quantities_and_names"), value: 0 },
        { text: this.$t("names_only"), value: 1 },
        { text: this.$t("quantities_only"), value: 2 },
        { text: this.$t("spare_meals"), value: 3 },
      ];
    },
    restaurants() {
      const uniqueRestaurants = [
        ...new Set(
          this.ordersByDates.map((order) => order.restaurant_name).filter(Boolean)
        ),
      ];
      return uniqueRestaurants.sort();
    },
    filteredOrdersByDates() {
      if (!this.selectedRestaurants.length) {
        return this.ordersByDates;
      }
      return this.ordersByDates.filter((order) =>
        this.selectedRestaurants.includes(order.restaurant_name)
      );
    },
    ordersByDatesEntireMonth() {
      const allMonthDateEntries = [];

      for (const currentDateStr of this.monthDates) {
        const currentDate = new Date(currentDateStr);

        const existingOrderDay = this.filteredOrdersByDates.find(
          (order) => order.dateFull === currentDateStr
        );

        if (existingOrderDay) {
          allMonthDateEntries.push(existingOrderDay);
        } else {
          const defaultEntry = {
            date: currentDate.getDate() + ".",
            shift: null,
            total: 0,
            dateFull: currentDateStr,
          };
          this.mealCategories.forEach((category) => {
            defaultEntry[category] = [];
          });
          allMonthDateEntries.push(defaultEntry);
        }
      }
      return allMonthDateEntries;
    },
    filters() {
      return this.$store.state.report.filters;
    },
    month() {
      return parseInt(this.date.split("-")[1]);
    },
    year() {
      return parseInt(this.date.split("-")[0]);
    },
    headers() {
      const items = [
        { text: this.$t("date"), value: "date" },
        { text: this.$t("shift.shift"), value: "shift" },
      ];

      items.push(
        ...this.mealCategories.map((category) => {
          return {
            text: this.toLatin(category),
            value: category || "",
          };
        }),
        { text: this.$t("number_of_foods"), value: "total" },
        { text: this.$t("number_of_orders"), value: "totalOrders" },
        { text: this.$t("total"), value: "rowTotal" }
      );
      if (!this.mealCategories.some(Boolean)) {
        items.splice(2, 0, {
          text: this.$t("meal"),
          value: "meal_name",
        });
      }
      return items;
    },
    formatMealsForPrint(meals) {
      if (!Array.isArray(meals)) return "";
      switch (this.selectedLayout) {
        case 0:
          return meals.map((m) => `${m.quantity}x ${this.toLatin(m.name)}`).join(", ");
        case 1:
          return meals.map((m) => this.toLatin(m.name)).join(", ");
        case 2:
          return meals.map((m) => m.quantity).join(", ");
        default:
          return meals.map((m) => m.label).join(", ");
      }
    },
    printItems() {
      const items = this.showAllDates
        ? this.ordersByDatesEntireMonth
        : this.ordersByDates;
      return items.map((item) => {
        const newItem = { ...item };
        this.mealCategories.forEach((category) => {
          newItem[category] = this.formatMealsForPrint(item[category]);
        });
        newItem.rowTotal = item.total * item.totalOrders;
        return newItem;
      });
    },
    printTotalRow() {
      const totalRow = {
        date: this.$t("total"),
        shift: "",
      };
      if (!this.mealCategories.some(Boolean)) {
        totalRow.meal_name = "";
      }
      this.mealCategories.forEach((category) => {
        totalRow[category] = this.totalByCategory[category];
      });
      totalRow.total = this.totalQuantity;
      totalRow.totalOrders = this.filteredOrdersByDates.reduce(
        (sum, row) => sum + row.totalOrders,
        0
      );
      totalRow.rowTotal = this.totalQuantity;
      return totalRow;
    },
    totalByCategory() {
      const totals = {};
      this.mealCategories.forEach((category) => {
        totals[category] = this.filteredOrdersByDates.reduce((sum, row) => {
          const arr = row[category];
          if (Array.isArray(arr)) {
            return (
              sum +
              arr.reduce((catSum, meal) => {
                return catSum + (meal.quantity || 0);
              }, 0)
            );
          }
          return sum;
        }, 0);
      });
      return totals;
    },
    totalQuantity() {
      return this.filteredOrdersByDates.reduce(
        (sum, row) => sum + (parseInt(row.total) || 0),
        0
      );
    },
    totalOrders() {
      return this.filteredOrdersByDates.reduce((sum, row) => {
        return sum + (parseInt(row.total) || 0) * (parseInt(row.totalOrders) || 0);
      }, 0);
    },
  },
  methods: {
    print() {
      this.printDocument({
        tables: [
          {
            headers: this.headers,
            items: [...this.printItems, this.printTotalRow],
          },
        ],
        title: `${this.toLatin(this.employee.name)} ${this.toLatin(
          this.employee.lastname
        )}`,
        subtitle: [this.monthDates[0], this.monthDates.slice(-1)[0]]
          .map((date) => {
            return this.localeDate(date);
          })
          .join(" - "),
      });
    },
    async loadReport() {
      if (!this.employee) return;
      this.loading = true;
      this.ordersByDates = [];
      const url = this.groupMeal ? "/user/orders-by-month" : "/user/food-orders-by-month";
      try {
        // Use reportService for the API call
        const response = await reportService.getEmployeeOrdersByMonthCustom(url, {
          month: this.month,
          year: this.year,
          user_id: this.employee.id,
        });
        const data = response.data;
        const dates = [...new Set(data.map((item) => item.date))];
        this.setMealCategories(data);

        this.ordersByDates = dates.map((date) => {
          const dayOrders = data.filter((item) => item.date === date);
          const sortedOrders = (dayOrders || []).sort((a, b) => b.quantity - a.quantity);
          const dailyOrders = {
            date: new Date(date).getDate() + ".",
            shift: dayOrders[0]?.shift,
            restaurant_name: dayOrders[0]?.restaurant_name || "",
            total: dayOrders.reduce((sum, item) => sum + item.quantity, 0),
            meal_name: dayOrders[0].meal_name || dayOrders[0].food_name || "",
            totalOrders: sortedOrders.length ? sortedOrders[0].quantity : 0,
            dateFull: date,
          };
          this.mealCategories.forEach((category) => {
            dailyOrders[category] = this.getDailyDish(dayOrders, category);
          });
          return dailyOrders;
        });
      } catch (error) {
        this.showErrorSnackbar(error, this.$t("error_loading_report"));
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    employee: {
      handler(newVal) {
        if (newVal) {
          this.loadReport();
        }
      },
      immediate: true,
    },
    date: {
      handler() {
        this.loadReport();
      },
    },
    restaurants: {
      handler(newRestaurants) {
        if (
          newRestaurants &&
          newRestaurants.length > 0 &&
          this.selectedRestaurants.length === 0
        ) {
          this.selectedRestaurants = [...newRestaurants];
        }
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
.v-data-table >>> tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.05) !important;
}

table tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.05) !important;
}

div.v-select__selections span.v-size--small span.v-chip__content {
  font-size: 10px !important;
}

.v-alert__icon.v-icon {
  font-size: 15px !important;
}

.order-history-header {
  background-color: rgb(255, 248, 245);
  box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2);
  padding: 10px;
  display: flex;
  align-content: center;
  height: 64px;
}

.v-input--checkbox {
  margin-top: 32px;
  padding-top: 0;
}
</style>

<style>
.show-all-checkbox .v-icon {
  margin-top: 4px !important;
}
</style>
